import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { currency, num } from "@/lib/format";
import { RunBadge, SkuTag } from "@/components/Badges";
import { PageHeader } from "@/components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClipboardList, ChevronRight, Leaf, Package, DollarSign, Boxes } from "lucide-react";

const KIND = {
  ingredient: { label: "Ingredient", icon: Leaf, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  packaging: { label: "Packaging", icon: Package, cls: "text-blue-700 bg-blue-50 border-blue-200" },
  cost: { label: "Cost", icon: DollarSign, cls: "text-stone-600 bg-stone-100 border-stone-200" },
};

const money4 = (n) => "$" + (n || 0).toFixed(n < 1 ? 4 : 2);

export default function BOMs() {
  const [boms, setBoms] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { api.get("/boms").then((r) => setBoms(r.data)); }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader title="Bills of Materials" subtitle={`${boms.length} recipes · per-unit COGS from Fun Guy BOM 2026 V7`} />

      <div className="rounded-lg border border-stone-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
              <th className="px-4 py-3 text-left">BOM ID</th>
              <th className="px-4 py-3 text-left">Product</th>
              <th className="px-4 py-3 text-left">Basis</th>
              <th className="px-4 py-3 text-right">Unit COGS</th>
              <th className="px-4 py-3 text-right">Wholesale / Sell</th>
              <th className="px-4 py-3 text-right">Can Build</th>
              <th className="px-4 py-3 text-right">Ingredients</th>
              <th className="px-4 py-3 text-right">Packaging</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {boms.map((b) => (
              <tr key={b.bom_code} data-testid={`bom-row-${b.bom_code}`} onClick={() => setActive(b)}
                className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70 cursor-pointer transition-colors">
                <td className="px-4 py-3"><SkuTag code={b.bom_code} /></td>
                <td className="px-4 py-3 font-medium text-stone-800">{b.target_name}</td>
                <td className="px-4 py-3 text-stone-500 text-xs">{b.batch_basis}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold text-stone-900">{currency(b.unit_cogs)}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-600">{currency(b.wholesale_cogs)}</td>
                <td className="px-4 py-3 text-right">
                  {b.buildable_units === null ? <span className="text-stone-300">—</span> : (
                    <span data-testid={`bom-buildable-${b.bom_code}`} className={`font-mono tabular-nums font-semibold ${b.buildable_units === 0 ? "text-rose-600" : b.buildable_units < 200 ? "text-amber-600" : "text-emerald-700"}`}>{num(b.buildable_units)}</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-600">{b.ingredient_count}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-stone-600">{b.packaging_count}</td>
                <td className="px-4 py-3"><RunBadge status={b.status} /></td>
                <td className="px-4 py-3 text-stone-400"><ChevronRight className="h-4 w-4" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl bg-white">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-stone-900">
                  <ClipboardList className="h-5 w-5 text-amber-700" /> {active.target_name}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-wrap gap-4 text-sm border-b border-stone-100 pb-3">
                <div><span className="text-stone-400 text-xs uppercase tracking-wide">BOM</span><div><SkuTag code={active.bom_code} /></div></div>
                <div><span className="text-stone-400 text-xs uppercase tracking-wide">Basis</span><div className="text-stone-700">{active.batch_basis}</div></div>
                <div><span className="text-stone-400 text-xs uppercase tracking-wide">Unit COGS</span><div className="font-mono font-semibold">{currency(active.unit_cogs)}</div></div>
                <div><span className="text-stone-400 text-xs uppercase tracking-wide">Wholesale / Sell</span><div className="font-mono font-semibold">{currency(active.wholesale_cogs)}</div></div>
              </div>
              {active.buildable_units !== null && (
                <div data-testid="bom-buildable-banner" className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${active.buildable_units === 0 ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
                  <Boxes className="h-4 w-4 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold">{active.buildable_units === 0 ? "Cannot build right now" : `You can build ${num(active.buildable_units)} units`}</span>
                    {active.limiting.length > 0 && (
                      <span> — limited by {active.limiting.map((l) => `${l.name} (${num(l.on_hand)} ${l.unit} on hand)`).join(", ")}</span>
                    )}
                  </div>
                </div>
              )}
              <div className="max-h-80 overflow-y-auto -mx-1">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 border-b border-stone-200 sticky top-0 bg-white">
                    <th className="px-2 py-2 text-left">Component</th><th className="px-2 py-2 text-left">Type</th>
                    <th className="px-2 py-2 text-right">Qty</th><th className="px-2 py-2 text-right">Cost/Unit</th>
                    <th className="px-2 py-2 text-right">Waste</th><th className="px-2 py-2 text-right">Ext. Cost</th></tr></thead>
                  <tbody>
                    {active.components.map((c, i) => {
                      const k = KIND[c.kind] || KIND.ingredient;
                      const Icon = k.icon;
                      return (
                        <tr key={i} className={`border-b border-stone-50 ${c.is_limiting ? "bg-rose-50/60" : ""}`}>
                          <td className="px-2 py-2">
                            <div className="font-medium text-stone-800 flex items-center gap-2">{c.name}
                              {c.is_limiting && <span className="text-[10px] font-bold uppercase tracking-wide text-rose-700 bg-rose-100 border border-rose-200 px-1.5 py-0.5 rounded">Limiting</span>}
                            </div>
                            {c.sku_code && <SkuTag code={c.sku_code} />}
                            {c.available_units !== undefined && <span className="ml-2 text-xs text-stone-400">covers {num(c.available_units)} units</span>}
                          </td>
                          <td className="px-2 py-2"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${k.cls}`}><Icon className="h-3 w-3" />{k.label}</span></td>
                          <td className="px-2 py-2 text-right font-mono tabular-nums">{num(c.qty)} <span className="text-stone-400">{c.unit}</span></td>
                          <td className="px-2 py-2 text-right font-mono tabular-nums text-stone-500">{c.unit === "mg" ? money4(c.cost) : currency(c.cost)}</td>
                          <td className="px-2 py-2 text-right font-mono tabular-nums text-stone-400">{c.waste ? `${(c.waste * 100).toFixed(0)}%` : "—"}</td>
                          <td className="px-2 py-2 text-right font-mono tabular-nums font-semibold text-stone-700">{currency(c.ext)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 border-t border-stone-100 pt-3 text-sm">
                <div><span className="text-stone-400 text-xs uppercase tracking-wide">Ingredients</span><div className="font-mono font-semibold text-emerald-700">{currency(active.raw_cost)}</div></div>
                <div><span className="text-stone-400 text-xs uppercase tracking-wide">Packaging</span><div className="font-mono font-semibold text-blue-700">{currency(active.packaging_cost)}</div></div>
                <div><span className="text-stone-400 text-xs uppercase tracking-wide">Other</span><div className="font-mono font-semibold text-stone-600">{currency(active.other_cost)}</div></div>
                <div><span className="text-stone-400 text-xs uppercase tracking-wide">Total COGS</span><div className="font-mono font-bold text-stone-900">{currency(active.unit_cogs)}</div></div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
